import { h, Fragment } from 'preact'
import { useSignal } from '@preact/signals'
import { useEffect, useRef } from 'preact/hooks'
import { RoomItem } from './RoomItem.jsx'
import { NewDirectModal } from './NewDirectModal.jsx'
import { NewGroupModal } from './NewGroupModal.jsx'
import { chatApi } from '../chat-api.js'
import { t } from '../chat-i18n.js'

const DEBOUNCE_MS = 400

async function refreshRooms(store) {
  try {
    const res = await chatApi.rooms.list()
    if (res.ok) {
      const data = await res.json
      store.rooms.value = Array.isArray(data) ? data : (data.rooms ?? [])
      store.roomsLoadError.value = null
    } else {
      store.roomsLoadError.value = t('chat.rooms_load_error')
    }
  } catch {
    store.roomsLoadError.value = t('chat.rooms_load_error')
  }
}

/**
 * RoomList — sidebar room list with local name filter + debounced API search.
 *
 * Props: { store, cable, logoUrl?, showBack? }
 *   logoUrl  — chat.* product host brand (before CHAT label)
 *   showBack — app.* fullscreen: back link before CHAT (instead of logo)
 */
export function RoomList({ store, cable, logoUrl = '', showBack = false }) {
  const searchText    = useSignal('')
  const searchResults = useSignal(null)
  const debounceTimer = useRef(null)
  const showDirect    = useSignal(false)
  const showGroup     = useSignal(false)

  const rooms        = store.rooms.value
  const activeRoomId = store.activeRoomId.value
  const roomsLoading = store.roomsLoading.value
  const roomsLoadError = store.roomsLoadError.value

  useEffect(() => {
    const q = searchText.value.trim()

    if (debounceTimer.current) clearTimeout(debounceTimer.current)

    if (!q) {
      searchResults.value = null
      return
    }

    debounceTimer.current = setTimeout(() => {
      chatApi.search.rooms(q).then(async res => {
        if (res.ok) {
          const data = await res.json
          searchResults.value = Array.isArray(data) ? data : (data.rooms ?? [])
        }
      }).catch(() => { /* ignore */ })
    }, DEBOUNCE_MS)
  }, [searchText.value]) // eslint-disable-line

  const q = searchText.value.trim().toLowerCase()
  const displayRooms = searchResults.value != null
    ? searchResults.value
    : q
      ? rooms.filter(r => (r.name || r.display_name || '').toLowerCase().includes(q))
      : rooms

  function handleRoomClick(room) {
    if (typeof store.navigateToRoom === 'function') {
      store.navigateToRoom(room.id)
    } else {
      store.activeRoomId.value = room.id
    }
  }

  async function handleRoomCreated(room) {
    if (room?.id) {
      const list = store.rooms.value.filter(r => r.id !== room.id)
      store.rooms.value = [room, ...list]
      await refreshRooms(store)
      handleRoomClick(room)
    }
  }

  return (
    <div class="chat-sidebar-inner">
      <div class="chat-sidebar-header">
        <div class="chat-sidebar-brand">
          {showBack ? (
            <a
              href="/"
              class="chat-sidebar-back"
              title={t('chat.back_to_app')}
              aria-label={t('chat.back_to_app')}
            >
              <i class="bi bi-arrow-left" aria-hidden="true" />
            </a>
          ) : logoUrl ? (
            <img src={logoUrl} alt="" class="chat-sidebar-logo" width="22" height="22" />
          ) : null}
          <span class="chat-sidebar-title">{t('chat.title')}</span>
        </div>
        <div class="chat-sidebar-header-actions">
          {store.canManageRooms && (
            <>
              <button
                type="button"
                class="chat-sidebar-action-btn"
                title={t('chat.new_direct_title')}
                onClick={() => { showDirect.value = true }}
              >
                <i class="bi bi-person-plus" />
              </button>
              <button
                type="button"
                class="chat-sidebar-action-btn"
                title={t('chat.new_group_title')}
                onClick={() => { showGroup.value = true }}
              >
                <i class="bi bi-people" />
              </button>
            </>
          )}
        </div>
      </div>

      <div class="chat-sidebar-search">
        <i class="bi bi-search chat-sidebar-search-icon" />
        <input
          type="search"
          class="chat-sidebar-search-input"
          placeholder={t('chat.room_search_placeholder')}
          value={searchText.value}
          onInput={e => { searchText.value = e.target.value }}
        />
        {searchText.value && (
          <button
            type="button"
            class="chat-sidebar-search-clear"
            onClick={() => { searchText.value = ''; searchResults.value = null }}
          >
            <i class="bi bi-x" />
          </button>
        )}
      </div>

      <div class="chat-sidebar-rooms">
        {roomsLoading && rooms.length === 0 ? (
          <div class="chat-rooms-loader">
            <span class="spinner-border spinner-border-sm text-secondary" role="status" />
          </div>
        ) : roomsLoadError && rooms.length === 0 ? (
          <div class="chat-sidebar-empty chat-sidebar-error">
            <p class="mb-2">{roomsLoadError}</p>
            <button
              type="button"
              class="btn btn-sm btn-outline-secondary"
              onClick={() => {
                store.roomsLoading.value = true
                refreshRooms(store).finally(() => { store.roomsLoading.value = false })
              }}
            >
              {t('chat.rooms_load_retry')}
            </button>
          </div>
        ) : displayRooms.length === 0 && q ? (
          <div class="chat-sidebar-empty">{t('chat.search_no_results')}</div>
        ) : (
          displayRooms.map(room => (
            <RoomItem
              key={room.id}
              room={room}
              store={store}
              cable={cable}
              active={room.id === activeRoomId}
              onClick={() => handleRoomClick(room)}
            />
          ))
        )}
      </div>

      <NewDirectModal
        open={showDirect.value}
        onClose={() => { showDirect.value = false }}
        onCreated={handleRoomCreated}
      />
      <NewGroupModal
        open={showGroup.value}
        onClose={() => { showGroup.value = false }}
        onCreated={handleRoomCreated}
      />
    </div>
  )
}
